import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, X, Save, Building2, Coins, Tags } from "lucide-react";
import { toast } from "sonner";
import { useSettings } from "@/lib/settings";

export default function Settings() {
  const { settings, refresh } = useSettings();
  const [brand, setBrand] = useState(settings.brand);
  const [currency, setCurrency] = useState(settings.currency);
  const [categories, setCategories] = useState(settings.expense_categories || []);
  const [newCat, setNewCat] = useState("");
  const [presets, setPresets] = useState(settings.currency_presets || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setBrand(settings.brand);
    setCurrency(settings.currency);
    setCategories(settings.expense_categories || []);
    setPresets(settings.currency_presets || []);
  }, [settings]);

  const onCurrencyCode = (code) => {
    const found = presets.find(p => p.code === code);
    if (found) setCurrency(found);
  };

  const removeCat = (idx) => setCategories(prev => prev.filter((_, i) => i !== idx));
  const addCat = () => {
    const v = newCat.trim();
    if (!v) return;
    if (categories.some(c => c.toLowerCase() === v.toLowerCase())) {
      toast.error("Category already exists");
      return;
    }
    setCategories(prev => [...prev, v]);
    setNewCat("");
  };
  const moveCat = (idx, delta) => {
    setCategories(prev => {
      const next = [...prev];
      const j = idx + delta;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/settings", {
        brand,
        currency,
        expense_categories: categories,
      });
      await refresh();
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Layout
      title="Settings"
      subtitle="Workspace configuration"
      actions={
        <Button onClick={save} disabled={saving} className="h-10 px-4 rounded-xl btn-warm text-[13px] font-semibold" data-testid="settings-save-button">
          <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save changes"}
        </Button>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Brand */}
        <div className="tech-card p-5 sm:p-6 rise">
          <div className="flex items-center gap-2 overline mb-4"><Building2 className="h-3.5 w-3.5" /> Brand</div>
          <div className="space-y-3">
            <FieldRow label="Organisation name">
              <Input value={brand?.name || ""} onChange={e => setBrand({ ...brand, name: e.target.value })} data-testid="brand-name-input" />
            </FieldRow>
            <FieldRow label="Tagline">
              <Input value={brand?.tagline || ""} onChange={e => setBrand({ ...brand, tagline: e.target.value })} data-testid="brand-tagline-input" />
            </FieldRow>
            <FieldRow label="Letter mark (1 char)">
              <Input maxLength={1} value={brand?.letter || ""} onChange={e => setBrand({ ...brand, letter: e.target.value.toUpperCase() })} className="w-20" data-testid="brand-letter-input" />
            </FieldRow>
            <div className="pt-2">
              <div className="overline mb-2">Preview</div>
              <div className="flex items-center gap-3 p-3 border border-neutral-200 rounded-md">
                <div className="h-10 w-10 rounded-md bg-neutral-950 text-white grid place-items-center font-display font-semibold text-base">
                  {brand?.letter || "K"}
                </div>
                <div>
                  <div className="font-display font-semibold text-base leading-none">{brand?.name}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mt-1.5">{brand?.tagline}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Currency */}
        <div className="tech-card p-5 sm:p-6 rise rise-1">
          <div className="flex items-center gap-2 overline mb-4"><Coins className="h-3.5 w-3.5" /> Currency</div>
          <FieldRow label="Default currency">
            <Select value={currency?.code} onValueChange={onCurrencyCode}>
              <SelectTrigger data-testid="currency-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {presets.map(p => (
                  <SelectItem key={p.code} value={p.code}>
                    <span className="font-mono mr-2">{p.symbol}</span> {p.code} — {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FieldRow>
          <div className="mt-4 p-4 border border-neutral-200 rounded-md">
            <div className="overline">Sample</div>
            <div className="mt-2 font-display text-3xl font-semibold tracking-tight">
              {currency?.symbol}12,500.00
            </div>
            <div className="text-xs text-neutral-500 mt-1">{currency?.code} · {currency?.name}</div>
          </div>
          <p className="text-xs text-neutral-500 mt-3 leading-relaxed">
            All amounts shown across the dashboard, expense ledger, and reports will use this currency.
            Individual expense records keep the currency they were logged with for audit accuracy.
          </p>
        </div>

        {/* Categories */}
        <div className="tech-card p-5 sm:p-6 rise rise-2">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 overline"><Tags className="h-3.5 w-3.5" /> Expense categories</div>
            <span className="text-xs text-neutral-500 font-mono">{categories.length}</span>
          </div>
          <div className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1">
            {categories.map((c, i) => (
              <div key={`${c}-${i}`} className="group flex items-center gap-2 border border-neutral-200 rounded-md py-2 px-3 hover:bg-neutral-50" data-testid={`category-row-${i}`}>
                <span className="font-mono text-[10px] text-neutral-400 w-6">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-sm flex-1 truncate">{c}</span>
                <div className="opacity-0 group-hover:opacity-100 flex gap-0.5 transition-opacity">
                  <button onClick={() => moveCat(i, -1)} disabled={i === 0} className="h-6 w-6 grid place-items-center text-neutral-500 hover:text-neutral-900 disabled:opacity-30" title="Move up">↑</button>
                  <button onClick={() => moveCat(i, 1)} disabled={i === categories.length - 1} className="h-6 w-6 grid place-items-center text-neutral-500 hover:text-neutral-900 disabled:opacity-30" title="Move down">↓</button>
                  <button onClick={() => removeCat(i)} className="h-6 w-6 grid place-items-center text-red-600 hover:bg-red-50 rounded" data-testid={`category-remove-${i}`}>
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-3 pt-3 border-t border-neutral-200">
            <Input
              placeholder="Add a category…"
              value={newCat}
              onChange={(e) => setNewCat(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addCat()}
              data-testid="category-add-input"
            />
            <Button onClick={addCat} variant="outline" data-testid="category-add-button">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function FieldRow({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="overline text-[10px]">{label}</Label>
      {children}
    </div>
  );
}
